import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { TableRow, SkeletonTableRow } from './TableRow';
import { hasValue } from '@utils';
import { remToPx } from '@hooks/useRemToPx';
import { Dropdown } from '@widget/popups/Dropdown';
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { DndContext, closestCenter } from '@dnd-kit/core';
import useDragSensors from '@hooks/touch/useDragSensors';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import ActionsListComponent from "./ActionListComponent";
import IconButton from '@widget/buttons/IconButton';
import Checkbox from '@widget/forms/inputs/Checkbox';
import ActionsSheet from '@widget/popups/ActionsSheet';
import { RowActionButtons, summariseRowActions } from './cardColumn';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import { UXPContext } from '@core/context/Context';
import classNames from 'classnames';
import InfiniteFooter from './InfiniteFooter';
/**
 * @export
 * Width reserved for the editable actions column (Save/Cancel/Delete buttons)
 */
const ACTION_COLUMN_WIDTH = 300;
const RowActionsDropdown = ({ actions, item }) => {
    const rowDropdownRef = useRef(null);
    const resolved = typeof actions === 'function' ? actions(item) : actions;
    if (Array.isArray(resolved)) {
        const visible = resolved.filter(a => !a.visible || a.visible(item));
        if (visible.length === 0)
            return null;
    }
    else if (!resolved) {
        return null;
    }
    return (_jsx(Dropdown, { ref: rowDropdownRef, trigger: _jsx("div", { className: "uxpcore_table__actions-actionbtn", children: _jsx(FontAwesomeIconWrapper, { icon: ["fal", "ellipsis-h"] }) }), content: _jsx(ActionsListComponent, { actions: actions, item: item, parentDropdownRef: rowDropdownRef }) }));
};
export const processColumns = (params) => {
    var _a, _b;
    const { columns: rawColumns, defaultColumns, containerWidth, minCellWidth: rawMinCellWidth, columnsToHide, selectedCount, totalCount, resizedColumns, editableMode } = params;
    if (!containerWidth)
        return null;
    // Numeric widths are px at the 13px base font (1440 wide). Scale them with the base font so
    // fixed columns grow with the screen like the rest of the UI. User-resized widths stay as set.
    const scale = remToPx(1) / 13;
    const px = (value) => (hasValue(value) ? value * scale : value);
    const scaleColumn = (col) => (col ? Object.assign(Object.assign({}, col), { width: px(col.width) }) : col);
    const columns = rawColumns.map(c => (Object.assign(Object.assign({}, c), { minWidth: px(c.minWidth), maxWidth: px(c.maxWidth) })));
    const minCellWidth = px(rawMinCellWidth);
    const editColumn = scaleColumn(defaultColumns.editColumn);
    const deleteColumn = scaleColumn(defaultColumns.deleteColumn);
    const selectColumn = scaleColumn(defaultColumns.selectColumn);
    const expandColumn = scaleColumn(defaultColumns.expandColumn);
    const actionsColumn = scaleColumn(defaultColumns.actionsColumn);
    const dragHandleColumn = scaleColumn(defaultColumns.dragHandleColumn);
    const reservedSpace = px(10)
        + ((editColumn === null || editColumn === void 0 ? void 0 : editColumn.enable) ? (editColumn === null || editColumn === void 0 ? void 0 : editColumn.width) || px(60) : 0)
        + ((deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.enable) ? (deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.width) || px(60) : 0)
        + ((selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.enable) ? (selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.width) || px(40) : 0)
        + ((expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.enable) ? (expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.width) || px(40) : 0)
        + ((actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.enable) ? (actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.width) || px(40) : 0)
        + ((dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.enable) ? (dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.width) || px(40) : 0)
        + (editableMode ? px(ACTION_COLUMN_WIDTH) : 0);
    let availableSpace = containerWidth - reservedSpace;
    const filteredColumns = (!!columnsToHide) ? columns.filter(c => !columnsToHide.includes(c.id)) : columns;
    const columnsWithMinWidth = filteredColumns.filter(column => hasValue(column.minWidth));
    const columnsWithoutMinWidth = filteredColumns.filter(column => !hasValue(column.minWidth));
    const allocatedWidth = columnsWithMinWidth.reduce((sum, column) => sum + (column.minWidth || 0), 0);
    availableSpace -= allocatedWidth;
    if (availableSpace < 0)
        availableSpace = 0;
    const remainingColumnWidth = Math.max((availableSpace / (columnsWithoutMinWidth.length || 1)), minCellWidth);
    const additionalSpace = (columnsWithoutMinWidth.length == 0) ? (availableSpace / columnsWithMinWidth.length || 1) : 0;
    const processedColumns = {};
    if (dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.enable) {
        processedColumns['__dragHandleColumn__'] = {
            id: '__dragHandleColumn__',
            label: dragHandleColumn.label || '',
            minWidth: dragHandleColumn.width,
            maxWidth: dragHandleColumn.width,
            width: dragHandleColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.enable) {
        processedColumns['__expandColumn__'] = {
            id: '__expandColumn__',
            label: expandColumn.label || '',
            minWidth: expandColumn.width,
            maxWidth: expandColumn.width,
            width: expandColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.enable) {
        processedColumns["__actionsColumn__"] = {
            id: "__actionsColumn__",
            label: (_jsx(Dropdown, { trigger: _jsx("div", { className: "uxpcore_table__actions-label", children: _jsx(FontAwesomeIconWrapper, { icon: ["fal", "sliders-h"] }) }), content: _jsx(ActionsListComponent, { actions: (_a = actionsColumn.headerActions) !== null && _a !== void 0 ? _a : [] }), preventOpening: ((_b = actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.headerActions) === null || _b === void 0 ? void 0 : _b.length) == 0, ref: actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.ref })),
            renderColumn: (item) => {
                var _a;
                return (_jsx(RowActionsDropdown, { actions: (_a = actionsColumn.rowActions) !== null && _a !== void 0 ? _a : [], item: item }));
            },
            rowActions: actionsColumn.rowActions,
            minWidth: actionsColumn.width,
            maxWidth: actionsColumn.width,
            width: actionsColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.enable) {
        processedColumns['__selectColumn__'] = {
            id: '__selectColumn__',
            label: _jsx(Checkbox, { checked: selectedCount > 0 && totalCount > 0 ? (selectedCount == totalCount ? true : 'intermediate') : false, onChange: (v) => { var _a; return (_a = selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.onSelectAll) === null || _a === void 0 ? void 0 : _a.call(selectColumn, v); }, type: 'bordered', className: 'uxpcore_table__select-checkbox' }),
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
            minWidth: selectColumn.width,
            maxWidth: selectColumn.width,
            width: selectColumn.width,
            isResizable: false,
        };
    }
    filteredColumns.forEach(column => {
        const minW = hasValue(column.minWidth) ? (column.minWidth + additionalSpace) : remainingColumnWidth;
        let width = hasValue(resizedColumns === null || resizedColumns === void 0 ? void 0 : resizedColumns[column.id]) ? resizedColumns[column.id] : minW;
        // Enforce maxWidth constraint
        if (hasValue(column.maxWidth) && width > column.maxWidth) {
            width = column.maxWidth;
        }
        processedColumns[column.id] = Object.assign(Object.assign({}, column), { width: width, minWidth: minW, maxWidth: column.maxWidth, isResizable: (column === null || column === void 0 ? void 0 : column.isResizable) || false, isSortable: (column === null || column === void 0 ? void 0 : column.isSortable) || false });
    });
    if (editColumn === null || editColumn === void 0 ? void 0 : editColumn.enable) {
        processedColumns['__editColumn__'] = {
            id: '__editColumn__',
            label: editColumn.label || 'Edit',
            renderColumn: editColumn.renderColumn || ((item) => {
                // Check visibility
                if (editColumn.visible && !editColumn.visible(item)) {
                    return null;
                }
                return _jsx(IconButton, { type: 'edit', onClick: () => { var _a; return (_a = editColumn.onEdit) === null || _a === void 0 ? void 0 : _a.call(editColumn, item); }, variant: 'secondary' });
            }),
            rowAction: editColumn.onEdit ? { label: editColumn.label, onClick: editColumn.onEdit, visible: editColumn.visible } : undefined,
            minWidth: editColumn.width,
            maxWidth: editColumn.width,
            width: editColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.enable) {
        processedColumns['__deleteColumn__'] = {
            id: '__deleteColumn__',
            label: deleteColumn.label || 'Delete',
            renderColumn: deleteColumn.renderColumn || ((item) => {
                // Check visibility
                if (deleteColumn.visible && !deleteColumn.visible(item)) {
                    return null;
                }
                return _jsx(IconButton, { type: 'delete', onClick: () => { var _a; return (_a = deleteColumn.onDelete) === null || _a === void 0 ? void 0 : _a.call(deleteColumn, item); }, variant: 'danger' });
            }),
            rowAction: deleteColumn.onDelete ? { label: deleteColumn.label, onClick: deleteColumn.onDelete, visible: deleteColumn.visible } : undefined,
            minWidth: deleteColumn.width,
            maxWidth: deleteColumn.width,
            width: deleteColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (editableMode) {
        processedColumns['__editActionsColumn__'] = {
            id: '__editActionsColumn__',
            label: ' ',
            minWidth: px(ACTION_COLUMN_WIDTH),
            maxWidth: px(ACTION_COLUMN_WIDTH),
            width: px(ACTION_COLUMN_WIDTH),
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    return processedColumns;
};
const TableResizeHandler = memo(({ onResize }) => {
    const handleMouseDown = useCallback((e) => {
        var _a;
        const startX = e.clientX;
        const startWidth = ((_a = e.target.parentElement) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const handleMouseMove = (e) => {
            const diff = e.clientX - startX;
            onResize(startWidth + diff);
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [onResize]);
    return (_jsx("div", { className: "uxpcore_table__resize-handle", onMouseDown: handleMouseDown }));
});
/**
 * @export
 * Removes columns with no sort order (0) from the sort configuration
 *
 * sort - The sort configuration to clean
 * returns Cleaned sort configuration with only sorted columns
 *
 * @example
 * ```tsx
 * const cleaned = cleanSort({ name: 1, age: 0, status: -1 });
 * // Result: { name: 1, status: -1 }
 * ```
 */
export function cleanSort(sort) {
    return Object.fromEntries(Object.entries(sort || {}).filter(([_, order]) => order !== 0));
}
/**
 * @export
 * Checks if any columns are currently sorted
 *
 * sort - The sort configuration to check
 * returns True if at least one column has a sort order
 *
 * @example
 * ```tsx
 * hasSort({ name: 1, age: 0 }) // true
 * hasSort({ name: 0, age: 0 }) // false
 * ```
 */
export function hasSort(sort) {
    const cleaned = cleanSort(sort);
    return !!Object.keys(cleaned).length;
}
/**
 * @export
 * Returns the number of columns currently sorted
 *
 * sort - The sort configuration
 * returns Count of sorted columns
 *
 * @example
 * ```tsx
 * getSortCount({ name: 1, age: -1, status: 0 }) // 2
 * ```
 */
export function getSortCount(sort) {
    const cleaned = cleanSort(sort);
    return Object.keys(cleaned || {}).length;
}
const TableSortHandler = memo(({ isSortable, sortOrder, onChange, children }) => {
    if (!isSortable)
        return children;
    return _jsxs("div", { className: "uxpcore_table__sort-handle", onClick: () => {
            onChange(sortOrder == 0 ? 1 : sortOrder == 1 ? -1 : 0);
        }, children: [_jsx("div", { className: "uxpcore_table__sort-handle-content", children: children }), sortOrder !== 0 &&
                _jsx("div", { className: "uxpcore_table__sort-handle-indicator", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', sortOrder == 1 ? 'caret-up' : 'caret-down'] }) })] });
});
// Columns phones leave out: their actions move to the row actions sheet (swipe left) and
// selection to select mode (long press)
const MOBILE_HIDDEN_COLUMNS = ['__editColumn__', '__deleteColumn__', '__actionsColumn__', '__selectColumn__'];
/**
 * Internal: the table engine under ObjectSearchComponent and SimpleConfigurationTable. Not exported to apps.
 *
 * Core table component that displays tabular data with rich features including sorting, resizing,
 * selection, expansion, and inline editing capabilities.
 *
 * Features:
 * - Column sorting (single and multi-column)
 * - Column resizing (resets on refresh)
 * - Row selection
 * - Expandable child rows
 * - Inline editing mode
 * - Custom renderers for cells, rows, and headers
 * - Loading states with skeleton rows
 * - Action columns (edit, delete, custom actions)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => console.log('Resized', id, width)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With sorting
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   sort={{ name: 1, age: -1 }}
 *   multiColumnSort={true}
 *   onChangeSort={(newSort) => setSort(newSort)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With editable mode
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   editable={{
 *     onAddItem: async (index, item) => {
 *       await api.create(item);
 *       return true;
 *     },
 *     onUpdateItem: async (index, item) => {
 *       await api.update(item);
 *       return true;
 *     },
 *     onDeleteItem: async (index, item) => {
 *       await api.delete(item);
 *       return true;
 *     },
 *     onCancelNewItem: () => console.log('Cancelled new item'),
 *     onCancelItem: () => console.log('Cancelled edit')
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With selection and click handlers
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   onClickRow={(e, row) => console.log('Clicked', row)}
 *   isRowSelected={(row) => selectedIds.includes(row.id)}
 *   onSelectRow={(row) => toggleSelection(row.id)}
 * />
 * ```
 */
const BaseTableComponent = ({ columns: allColumns, data, loading, noItemsMessage = 'No items found', totalWidth: allColumnsWidth, renderRow, renderChildRows, renderCell, onClickRow, isRowSelected, onSelectRow, renderHeader, handleResize, sort, multiColumnSort, onChangeSort, editable, hideHeader, rowTitle, scrollRef, onScroll, infinite }) => {
    var _a, _b, _c;
    const context = useContext(UXPContext);
    const [internalData, setInternalData] = useState(data);
    const isMobile = useIsMobileLayout();
    // Phones: select mode is on while any row on the page is selected (long press starts it,
    // unselecting the last row ends it)
    const canSelect = !!(allColumns === null || allColumns === void 0 ? void 0 : allColumns.__selectColumn__) && !!onSelectRow;
    const selectMode = isMobile && canSelect && !!(internalData === null || internalData === void 0 ? void 0 : internalData.some(r => isRowSelected === null || isRowSelected === void 0 ? void 0 : isRowSelected(r)));
    const columns = useMemo(() => {
        if (!isMobile || !allColumns)
            return allColumns;
        const shown = Object.entries(allColumns).filter(([id]) => !MOBILE_HIDDEN_COLUMNS.includes(id) || (id === '__selectColumn__' && selectMode));
        // Give the width of the left-out columns back to the content columns, so rows use the full width
        const freed = Object.entries(allColumns)
            .filter(([id]) => !shown.some(([s]) => s === id))
            .reduce((sum, [, c]) => sum + (c.width || 0), 0);
        const growable = shown.filter(([id, c]) => !id.startsWith('__') && !hasValue(c.maxWidth));
        if (!freed || !growable.length)
            return Object.fromEntries(shown);
        const extra = freed / growable.length;
        return Object.fromEntries(shown.map(([id, c]) => growable.some(([g]) => g === id)
            ? [id, Object.assign(Object.assign({}, c), { width: c.width + extra, minWidth: (c.minWidth || 0) + extra })]
            : [id, c]));
    }, [allColumns, isMobile, selectMode]);
    // A header over a single column only repeats what the rows show: hidden on phones, and on
    // desktop when that column has no label and there are no action columns (a list drawn by one renderColumn)
    const contentColumns = columns ? Object.entries(columns).filter(([id]) => !id.startsWith('__')) : [];
    const singleColumnOnPhone = isMobile && !!columns && contentColumns.length <= 1;
    // Only for a plain list: with action columns (select all, the ⋯ menu, edit / delete) the header stays
    const singleUnlabeledColumn = contentColumns.length === 1 && !contentColumns[0][1].label
        && Object.keys(columns || {}).every(id => !id.startsWith('__'));
    const totalWidth = isMobile && columns ? Object.values(columns).reduce((sum, c) => sum + (c.width || 0), 0) : allColumnsWidth;
    // Phones: row actions sheet (edit, the row actions, delete), opened by swiping a row left
    const editAction = (_a = allColumns === null || allColumns === void 0 ? void 0 : allColumns.__editColumn__) === null || _a === void 0 ? void 0 : _a.rowAction;
    const deleteAction = (_b = allColumns === null || allColumns === void 0 ? void 0 : allColumns.__deleteColumn__) === null || _b === void 0 ? void 0 : _b.rowAction;
    const rowActions = (_c = allColumns === null || allColumns === void 0 ? void 0 : allColumns.__actionsColumn__) === null || _c === void 0 ? void 0 : _c.rowActions;
    const hasRowActions = !!(editAction || deleteAction || rowActions);
    const [sheet, setSheet] = useState(null);
    const closeSheet = useCallback(() => setSheet(s => s && Object.assign(Object.assign({}, s), { open: false })), []);
    const openSheet = useCallback((row) => setSheet({ row, open: true }), []);
    const startSelecting = useCallback((row) => { if (!(isRowSelected === null || isRowSelected === void 0 ? void 0 : isRowSelected(row)))
        onSelectRow === null || onSelectRow === void 0 ? void 0 : onSelectRow(row); }, [isRowSelected, onSelectRow]);
    const onSwipeLeft = isMobile && hasRowActions ? openSheet : undefined;
    const sheetActions = useMemo(() => ({ edit: editAction, rowActions, delete: deleteAction }), [editAction, rowActions, deleteAction]);
    // Phone cards: the row's one action (when it has an icon), otherwise ⋯ for the actions sheet
    const renderTouchActions = (row) => {
        const { empty, one } = summariseRowActions(row, sheetActions);
        if (empty)
            return null;
        if (one)
            return _jsx(IconButton, { icon: one.icon, ariaLabel: typeof one.label === 'string' ? context.$L(one.label) : undefined, variant: one.danger ? 'danger' : 'secondary', borderless: true, onClick: () => one.onClick(row) });
        return _jsx(IconButton, { icon: "phr dots-three", ariaLabel: context.$L('uxp-core.text.actions'), variant: "secondary", borderless: true, onClick: () => openSheet(row) });
    };
    const touchActions = isMobile && hasRowActions && !selectMode ? renderTouchActions : undefined;
    const onLongPress = isMobile && canSelect ? startSelecting : undefined;
    const renderRowActionsSheet = () => {
        if (!sheet)
            return null;
        // ActionsSheet closes when a button row is tapped, before its action runs
        return _jsx(ActionsSheet, { isOpen: sheet.open, onClose: closeSheet, title: rowTitle === null || rowTitle === void 0 ? void 0 : rowTitle(sheet.row), children: _jsx(RowActionButtons, { row: sheet.row, actions: sheetActions, onClose: closeSheet }) });
    };
    useEffect(() => {
        setInternalData(data);
    }, [data]);
    const sensors = useDragSensors({ keyboard: { coordinateGetter: sortableKeyboardCoordinates } });
    const handleDragEnd = (event) => {
        var _a;
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = internalData.findIndex((_, idx) => `row-${idx}` === active.id);
        const newIndex = internalData.findIndex((_, idx) => `row-${idx}` === over.id);
        const newData = arrayMove(internalData, oldIndex, newIndex);
        setInternalData(newData); // temporary update state
        (_a = editable === null || editable === void 0 ? void 0 : editable.onReorderItems) === null || _a === void 0 ? void 0 : _a.call(editable, newData);
    };
    const handleSort = (columnId, sortOrder) => {
        onChangeSort(multiColumnSort ? Object.assign(Object.assign({}, sort), { [columnId]: sortOrder }) : { [columnId]: sortOrder });
    };
    const defaultRenderHeader = () => (_jsx(_Fragment, { children: Object.values(columns).map((column) => (_jsxs("div", { className: `uxpcore_table__header-cell ${column.verticalLabel ? 'uxpcore_table__header-cell--vertical' : ''}`, style: {
                minWidth: column.minWidth,
                width: column.width,
                maxWidth: column.maxWidth
            }, children: [_jsx(TableSortHandler, { isSortable: (column === null || column === void 0 ? void 0 : column.isSortable) || false, sortOrder: (sort === null || sort === void 0 ? void 0 : sort[column.id]) || 0, onChange: (so) => { handleSort(column.id, so); }, children: _jsx("div", { className: "uxpcore_table__header-celllabel", children: column.label }) }), column.isResizable
                    ? _jsx(TableResizeHandler, { onResize: (width) => handleResize(column.id, width) })
                    : null] }, column.id))) }));
    const tableBody = (_jsx(_Fragment, { children: loading
            ? (Array.from({ length: 5 }).map((_, index) => (_jsx(SkeletonTableRow, { columns: columns }, index))))
            : (internalData === null || internalData === void 0 ? void 0 : internalData.length) === 0
                ? _jsx("div", { className: 'uxpcore_table__noitems', children: noItemsMessage })
                : (internalData.map((row, index) => {
                    var _a, _b, _c, _d, _e, _f;
                    return (renderRow
                        ? renderRow({
                            row,
                            columns,
                            onClickRow,
                            isRowSelected,
                            onSelectRow,
                            renderChildRows,
                            renderCell,
                            editable: ((editable === null || editable === void 0 ? void 0 : editable.onAddItem) || (editable === null || editable === void 0 ? void 0 : editable.onUpdateItem) || (editable === null || editable === void 0 ? void 0 : editable.onDeleteItem)) ? {
                                onAddRow: (_a = editable.onAddItem) === null || _a === void 0 ? void 0 : _a.bind(null, index),
                                onUpdateRow: (_b = editable.onUpdateItem) === null || _b === void 0 ? void 0 : _b.bind(null, index),
                                onDeleteRow: (_c = editable.onDeleteItem) === null || _c === void 0 ? void 0 : _c.bind(null, index),
                                onCancelNewRow: editable.onCancelNewItem,
                                onCancelEditing: editable.onCancelItem,
                                labels: editable.labels
                            } : undefined,
                            sortable: !!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems),
                            dragId: `row-${index}`,
                            onSwipeLeft,
                            onLongPress,
                            selectMode,
                            touchActions
                        })
                        : _jsx(TableRow
                        // A row being added keeps its key when rows load above it, so its inputs keep what is typed
                        , { row: row, columns: columns, onClickRow: onClickRow, isRowSelected: isRowSelected, onSelectRow: onSelectRow, renderChildRows: renderChildRows, renderCell: renderCell, editable: ((editable === null || editable === void 0 ? void 0 : editable.onAddItem) || (editable === null || editable === void 0 ? void 0 : editable.onUpdateItem) || (editable === null || editable === void 0 ? void 0 : editable.onDeleteItem)) ? {
                                onAddRow: (_d = editable.onAddItem) === null || _d === void 0 ? void 0 : _d.bind(null, index),
                                onUpdateRow: (_e = editable.onUpdateItem) === null || _e === void 0 ? void 0 : _e.bind(null, index),
                                onDeleteRow: (_f = editable.onDeleteItem) === null || _f === void 0 ? void 0 : _f.bind(null, index),
                                onCancelNewRow: editable.onCancelNewItem,
                                onCancelEditing: editable.onCancelItem,
                                labels: editable.labels
                            } : undefined, sortable: !!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems), dragId: `row-${index}`, onSwipeLeft: onSwipeLeft, onLongPress: onLongPress, selectMode: selectMode, touchActions: touchActions }, editable && row.__newitem__ ? '__newitem__' : index));
                })) }));
    return (_jsxs("div", { className: classNames("uxpcore_table__container", { "uxpcore_table__container--mobile": isMobile }), children: [_jsxs("div", { className: "uxpcore_table__table", style: { minWidth: totalWidth }, children: [!hideHeader && !singleColumnOnPhone && !singleUnlabeledColumn && _jsx("div", { className: "uxpcore_table__header", children: renderHeader ? renderHeader(columns, handleResize) : defaultRenderHeader() }), _jsxs("div", { className: "uxpcore_table__body", ref: scrollRef, onScroll: onScroll, children: [!!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems) && !loading && (internalData === null || internalData === void 0 ? void 0 : internalData.length) > 0 ? (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: internalData.map((_, i) => `row-${i}`), strategy: verticalListSortingStrategy, children: tableBody }) })) : tableBody, infinite && !loading && _jsx(InfiniteFooter, { paging: infinite })] })] }), renderRowActionsSheet()] }));
};
export default memo(BaseTableComponent);
